import { useCallback, useEffect, useRef, useState } from "react"
import { type Socket } from "socket.io-client"
import { getIntradaySocket, getOrCreateIntradaySocket } from "socket/intraday-connection"
import {
  addStandaloneSubscription,
  removeStandaloneSubscription,
  STANDALONE_PERFORMANCE_UPDATED_EVENT,
  type StandalonePerformanceByModelProviderId,
  type StandalonePerformanceByPeriod,
  type StandalonePeriod,
  subscribeToStandalonePerformance,
  unsubscribeFromStandalonePerformance,
} from "socket/intraday-standalone-performance"
import { computeListDiff } from "./list-diff"
import { useIntradaySocketConnection } from "./use-intraday-socket-connection"

export interface UseIntradayStandalonePerformanceSubscription {
  period: StandalonePeriod
  modelProviderIds: string[]
}

interface UseIntradayStandalonePerformanceStreamParams {
  modelId: string
  enabled: boolean
  subscriptions: UseIntradayStandalonePerformanceSubscription[]
}

export interface UseIntradayStandalonePerformanceStreamResult {
  standalonePerformanceByPeriod: StandalonePerformanceByPeriod
}

function getSortedUniqueIds(ids: string[]): string[] {
  return [...new Set(ids)].sort()
}

function buildSubscriptionsMap(
  subscriptions: UseIntradayStandalonePerformanceSubscription[],
): Map<StandalonePeriod, string[]> {
  return subscriptions.reduce((acc, { period, modelProviderIds }) => {
    acc.set(period, getSortedUniqueIds(modelProviderIds))
    return acc
  }, new Map<StandalonePeriod, string[]>())
}

interface SyncPeriodParams {
  socket: Socket
  period: StandalonePeriod
  prevIds: string[]
  nextIds: string[]
  modelId: string
  previousModelId: string
  forceFullResync: boolean
}

function syncPeriodSubscription({
  socket,
  period,
  prevIds,
  nextIds,
  modelId,
  previousModelId,
  forceFullResync,
}: SyncPeriodParams): void {
  const modelIdChanged = previousModelId !== modelId

  if (modelIdChanged && prevIds.length > 0) {
    unsubscribeFromStandalonePerformance(socket, previousModelId, period)
  }

  if (nextIds.length === 0) {
    if (prevIds.length > 0 && !modelIdChanged) {
      unsubscribeFromStandalonePerformance(socket, modelId, period)
    }
    return
  }

  if (forceFullResync || modelIdChanged || prevIds.length === 0) {
    subscribeToStandalonePerformance(socket, nextIds, modelId, period)
    return
  }

  const { added, removed } = computeListDiff(prevIds, nextIds)
  if (added.length > 0) addStandaloneSubscription(socket, added, modelId, period)
  if (removed.length > 0) removeStandaloneSubscription(socket, removed, modelId, period)
}

function mergeStandalonePerformance(
  prev: StandalonePerformanceByPeriod,
  payload: StandalonePerformanceByPeriod,
): StandalonePerformanceByPeriod {
  return Object.entries(payload || {}).reduce((next, [periodKey, updates]) => {
    next[periodKey] = {
      ...(next[periodKey] || {}),
      ...(updates as StandalonePerformanceByModelProviderId),
    }
    return next
  }, { ...prev })
}

function unsubscribeAllStandalonePerformance(
  socket: Socket,
  subscriptions: UseIntradayStandalonePerformanceSubscription[],
  modelId: string,
): void {
  for (const { period } of subscriptions) {
    unsubscribeFromStandalonePerformance(socket, modelId, period)
  }
}

export function useIntradayStandalonePerformanceStream({
  modelId,
  enabled,
  subscriptions,
}: UseIntradayStandalonePerformanceStreamParams): UseIntradayStandalonePerformanceStreamResult {
  const [standalonePerformanceByPeriod, setStandalonePerformanceByPeriod] = useState<StandalonePerformanceByPeriod>({})
  const previousSubscriptionsRef = useRef<UseIntradayStandalonePerformanceSubscription[]>([])
  const previousModelIdRef = useRef(modelId)

  const handleUnmount = useCallback<(socket: Socket | undefined, accountId: string) => void>((socket) => {
    if (socket && previousSubscriptionsRef.current.length > 0) {
      unsubscribeAllStandalonePerformance(socket, previousSubscriptionsRef.current, previousModelIdRef.current)
      previousSubscriptionsRef.current = []
    }
  }, [])

  const { accountId, registerSocketConsumer } = useIntradaySocketConnection(handleUnmount)

  useEffect(() => {
    if (!accountId) return

    if (!enabled || subscriptions.length === 0) {
      const modelIdForUnsubscribe = previousModelIdRef.current
      const socket = getIntradaySocket(accountId)
      if (socket && previousSubscriptionsRef.current.length > 0) {
        unsubscribeAllStandalonePerformance(socket, previousSubscriptionsRef.current, modelIdForUnsubscribe)
        previousSubscriptionsRef.current = []
      }
      return
    }

    registerSocketConsumer()
    const socket = getOrCreateIntradaySocket(accountId)

    const reconcileStandaloneSubscriptions = (forceFullResync = false): void => {
      const modelIdChanged = previousModelIdRef.current !== modelId
      const previousModelId = previousModelIdRef.current
      previousModelIdRef.current = modelId

      if (forceFullResync || modelIdChanged) {
        setStandalonePerformanceByPeriod({})
      }

      const previousByPeriod = buildSubscriptionsMap(previousSubscriptionsRef.current)
      const nextByPeriod = buildSubscriptionsMap(subscriptions)
      const allPeriods = new Set<StandalonePeriod>([
        ...previousSubscriptionsRef.current.map(({ period }) => period),
        ...subscriptions.map(({ period }) => period),
      ])

      for (const period of allPeriods) {
        syncPeriodSubscription({
          socket,
          period,
          prevIds: previousByPeriod.get(period) || [],
          nextIds: nextByPeriod.get(period) || [],
          modelId,
          previousModelId,
          forceFullResync,
        })
      }

      previousSubscriptionsRef.current = subscriptions.map(({ period, modelProviderIds }) => ({
        period,
        modelProviderIds: getSortedUniqueIds(modelProviderIds),
      }))
    }

    const handleConnect = (): void => reconcileStandaloneSubscriptions(true)

    const handleStandalonePerformanceUpdated = (payload: StandalonePerformanceByPeriod): void => {
      setStandalonePerformanceByPeriod(prev => mergeStandalonePerformance(prev, payload))
    }

    socket.on("connect", handleConnect)
    socket.on(STANDALONE_PERFORMANCE_UPDATED_EVENT, handleStandalonePerformanceUpdated)

    if (socket.connected) {
      reconcileStandaloneSubscriptions(false)
    }

    return () => {
      socket.off("connect", handleConnect)
      socket.off(STANDALONE_PERFORMANCE_UPDATED_EVENT, handleStandalonePerformanceUpdated)
    }
  }, [enabled, accountId, modelId, subscriptions, registerSocketConsumer])

  return { standalonePerformanceByPeriod }
}
